'use client';

import { cn, FormControl, FormField, FormItem } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { useRef, useState, type RefObject } from 'react';
import { get, useFormContext, useFormState } from 'react-hook-form';
import { FieldInfo } from '../FieldInfo/FieldInfo';
import { FieldMessage } from '../FieldMessage/FieldMessage';
import { TextInput } from '../TextInput/TextInput';
import type { DateOfBirthProps } from './types';

/**
 * DateOfBirth Component
 *
 * A composite form control for date of birth input with three fields: day, month, and year.
 *
 * Key Features:
 * - Uses React Hook Form with FormField/FormItem/FormControl for proper validation integration
 * - Day and month fields have special focus state management for better UX:
 *   - While focused: shows raw input (e.g., "1", "10") allowing free typing
 *   - When blurred: pads single digits with leading zero (e.g., "01", "10")
 *   - Cursor positioning: places caret at end when focusing back into field
 * - All fields use text input with numeric inputMode for mobile keyboard optimization
 * - Input filtering: strips non-digits and limits length (day/month: 2 chars, year: 4 chars)
 * - Validation: integrates with Zod schema via RHF, shows per-field error states
 * - Group-level errors (e.g. "Invalid date", underage) set on `namePrefix` mark all three fields as errored.
 *   RHF only re-validates the changed part, so the group is re-validated here whenever a part changes
 *   and the date is complete or a group error is showing (otherwise it would stay stale until submit)
 * - Empty/zero values are stored as `undefined` to trigger required validation
 *
 * Props:
 * - formId: Base ID for form accessibility
 * - label: Display label for the date group
 * - namePrefix: RHF field path prefix (e.g., "personalDetails.dateOfBirth")
 * - helpMessage: Optional group-level error message
 * - helpMessageType: Message type for styling
 */

const MAX_DAY = 31;
const MAX_MONTH = 12;
const MAX_DAY_MONTH_DIGITS = 2;
const MAX_YEAR_DIGITS = 4;

type HandleNumericDatePartChangeProps = {
  maxLength: number;
  maxAllowedValue: number;
  nextRef?: RefObject<HTMLInputElement | null>;
  onChange: (value: number | undefined) => void;
  setInputState: (value: string) => void;
};

const shouldAutoAdvance = (digits: string, maxAllowedValue: number) =>
  digits !== '' && digits !== '0' && (digits.length === MAX_DAY_MONTH_DIGITS || Number(digits) > Math.floor(maxAllowedValue / 10));

const handleNumericDatePartChange = (rawValue: string, { maxLength, maxAllowedValue, nextRef, onChange, setInputState }: HandleNumericDatePartChangeProps) => {
  const digits = rawValue.replace(/\D/g, '').slice(0, maxLength);
  setInputState(digits);

  if (digits === '' || digits === '0') {
    onChange(undefined);
  } else {
    onChange(Number(digits));
  }

  if (shouldAutoAdvance(digits, maxAllowedValue)) {
    nextRef?.current?.focus();
  }
};

function DateOfBirth({ formId, label, namePrefix, helpMessage, helpMessageMode = 'inline', helpMessageType }: DateOfBirthProps) {
  const t = useTranslations('form');
  const [isDayFocused, setIsDayFocused] = useState(false);
  const [isMonthFocused, setIsMonthFocused] = useState(false);
  const [dayInput, setDayInput] = useState('');
  const [monthInput, setMonthInput] = useState('');
  const dayRef = useRef<HTMLInputElement | null>(null);
  const monthRef = useRef<HTMLInputElement | null>(null);
  const yearRef = useRef<HTMLInputElement | null>(null);
  const { getFieldState, getValues, trigger } = useFormContext();
  const { errors } = useFormState({ name: namePrefix });
  // A real group FieldError has a `type`; a plain container of child errors does not
  const hasGroupError = Boolean(get(errors, namePrefix)?.type);

  const revalidateDate = () => {
    const { day, month, year } = getValues(namePrefix) ?? {};
    const isComplete = [day, month, year].every(part => typeof part === 'number');

    if (isComplete || getFieldState(namePrefix).error?.type) {
      void trigger(namePrefix);
    }
  };

  return (
    <div className="flex flex-col">
      <FieldInfo label={label} htmlFor={`${formId}-day`} />
      <div className="flex flex-row gap-2 peer/dob">
        <FormField
          name={`${namePrefix}.day`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormControl>
                <TextInput
                  {...field}
                  id={`${formId}-day`}
                  type="text"
                  autoComplete="bday-day"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={t('placeholders.day')}
                  helpMessageType={fieldState.error || hasGroupError ? 'error' : undefined}
                  ref={dayRef}
                  value={isDayFocused ? dayInput : typeof field.value === 'number' ? (field.value > 0 ? String(field.value).padStart(2, '0') : '') : (field.value ?? '')}
                  onChange={event =>
                    handleNumericDatePartChange(event.target.value, {
                      maxLength: MAX_DAY_MONTH_DIGITS,
                      maxAllowedValue: MAX_DAY,
                      nextRef: monthRef,
                      onChange: value => {
                        field.onChange(value);
                        revalidateDate();
                      },
                      setInputState: setDayInput,
                    })
                  }
                  onFocus={() => {
                    setIsDayFocused(true);
                    setDayInput(typeof field.value === 'number' ? String(field.value) : (field.value ?? ''));
                    requestAnimationFrame(() => {
                      const input = dayRef.current;
                      if (input) {
                        const len = (typeof field.value === 'number' ? String(field.value) : (field.value ?? '')).length;

                        input.setSelectionRange(len, len);
                      }
                    });
                  }}
                  onBlur={() => {
                    setIsDayFocused(false);
                    field.onBlur();
                  }}
                  data-track-name="DOB Day"
                  data-track-privacy="Mask"
                  data-testid="dob-day"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          name={`${namePrefix}.month`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormControl>
                <TextInput
                  {...field}
                  id={`${formId}-month`}
                  type="text"
                  autoComplete="bday-month"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={t('placeholders.month')}
                  helpMessageType={fieldState.error || hasGroupError ? 'error' : undefined}
                  ref={monthRef}
                  value={isMonthFocused ? monthInput : typeof field.value === 'number' ? (field.value > 0 ? String(field.value).padStart(2, '0') : '') : (field.value ?? '')}
                  onChange={event =>
                    handleNumericDatePartChange(event.target.value, {
                      maxLength: MAX_DAY_MONTH_DIGITS,
                      maxAllowedValue: MAX_MONTH,
                      nextRef: yearRef,
                      onChange: value => {
                        field.onChange(value);
                        revalidateDate();
                      },
                      setInputState: setMonthInput,
                    })
                  }
                  onFocus={() => {
                    setIsMonthFocused(true);
                    setMonthInput(typeof field.value === 'number' ? String(field.value) : (field.value ?? ''));
                    requestAnimationFrame(() => {
                      const input = monthRef.current;
                      if (input) {
                        const len = (typeof field.value === 'number' ? String(field.value) : (field.value ?? '')).length;

                        input.setSelectionRange(len, len);
                      }
                    });
                  }}
                  onBlur={() => {
                    setIsMonthFocused(false);
                    field.onBlur();
                  }}
                  data-track-name="DOB Month"
                  data-track-privacy="Mask"
                  data-testid="dob-month"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          name={`${namePrefix}.year`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormControl>
                <TextInput
                  {...field}
                  id={`${formId}-year`}
                  type="text"
                  autoComplete="bday-year"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={t('placeholders.year')}
                  helpMessageType={fieldState.error || hasGroupError ? 'error' : undefined}
                  ref={yearRef}
                  onChange={event => {
                    const digits = event.target.value.replace(/\D/g, '').slice(0, MAX_YEAR_DIGITS);
                    field.onChange(digits === '' ? undefined : Number(digits));
                    revalidateDate();
                  }}
                  onBlur={field.onBlur}
                  data-track-name="DOB Year"
                  data-track-privacy="Mask"
                  data-testid="dob-year"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <FieldMessage
        className={cn(helpMessageMode === 'inline-foreground' && 'peer-focus-within/dob:**:text-foreground', helpMessageMode === 'focusout' && 'peer-focus-within/dob:hidden')}
        id={`${formId}-dob-error`}
        message={helpMessage}
        messageType={helpMessageType}
        data-track-helper="DOB"
        data-testid="dob-error"
      />
    </div>
  );
}

export { DateOfBirth };
